import { useCallback, useEffect, useRef, useState } from 'react';
import { vibrar } from '../utils/haptics';

// ─── Arrastar para dispensar ─────────────────────────────────────────────────
// Gaveta lateral (arrastar para a direita) e prévia de foto (arrastar para
// baixo). Regras pensadas para não roubar gestos de ninguém:
//
// - Mouse nunca arrasta (arrastar com o cursor é acidental);
// - O gesto só começa quando a direção principal vence a perpendicular, então
//   a rolagem vertical da lista e a rolagem horizontal das chips continuam;
// - Elementos marcados com `data-no-swipe` são ignorados;
// - O deslocamento contrário ganha resistência, como numa folha elástica;
// - **O `touchmove` do gesto é consumido** (`preventDefault` em listener
//   não-passivo). Sem isso o Chromium mantém a sequência de toque "viva" e
//   **suprime o `click` do próximo toque por ~300-500 ms** — era o "preciso dar
//   dois ou três toques para abrir de novo" (ver docs/MOBILE.md).

/** Movimento mínimo (px) para decidir a direção do gesto. */
const TOLERANCIA = 8;
/** Quanto a direção principal precisa vencer a perpendicular. */
const RAZAO_EIXO = 1.2;
/** Velocidade (px/ms) que fecha mesmo sem percorrer o limite completo. */
const VELOCIDADE_FECHAR = 0.5;
/** Janela mínima para o cálculo de velocidade (evita divisão explosiva). */
const JANELA_VELOCIDADE_MS = 12;
/** Limite absoluto mais baixo aceito, em px. */
const LIMITE_MINIMO = 48;

const prefereMenosMovimento = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/** Animação de volta à origem quando o gesto não passa do limite. */
const TRANSICAO_VOLTA = 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)';

// Captura de ponteiro é opcional e pode lançar (`NotFoundError`/`InvalidState`)
// quando o ponteiro já foi liberado pelo navegador. Uma exceção aqui abortava o
// `finalizar` no meio e deixava a gaveta deslocada — por isso os dois lados
// vivem em `try/catch` e o estado do gesto é sempre limpo antes.
const capturarPonteiro = (alvo, pointerId) => {
  try {
    if (alvo?.setPointerCapture && !alvo.hasPointerCapture?.(pointerId)) {
      alvo.setPointerCapture(pointerId);
    }
  } catch (error) {
    // Sem captura o toque continua chegando enquanto o dedo está no overlay.
  }
};

const liberarPonteiro = (alvo, pointerId) => {
  try {
    if (alvo?.hasPointerCapture?.(pointerId)) alvo.releasePointerCapture(pointerId);
  } catch (error) {
    // `NotFoundError`: o navegador já liberou a captura no `pointerup`.
  }
};

/**
 * @param {object} opcoes
 * @param {boolean} [opcoes.aberto] Só aceita gesto com o overlay aberto.
 * @param {() => void} [opcoes.onFechar] Chamado quando o gesto passa do limite.
 * @param {'direita'|'baixo'} [opcoes.direcao] Sentido que fecha.
 * @param {number} [opcoes.limite] Distância fixa (px) para fechar.
 * @param {number} [opcoes.limiteRelativo] Fração do tamanho do elemento.
 * @param {number} [opcoes.resistencia] Fator aplicado no sentido contrário.
 * @param {boolean} [opcoes.apenasToque] Ignora mouse (padrão `true`).
 * @param {boolean} [opcoes.haptic] Vibra ao fechar.
 */
export const useArrastarParaFechar = ({
  aberto = true,
  onFechar,
  direcao = 'direita',
  limite = 90,
  limiteRelativo = 0.32,
  resistencia = 0.3,
  apenasToque = true,
  haptic = true,
} = {}) => {
  const [offset, setOffset] = useState(0);
  const [arrastando, setArrastando] = useState(false);
  // Painel já "saiu": fica fora da tela até o fechamento agendado rodar.
  const [fechando, setFechando] = useState(false);
  const gestoRef = useRef(null);
  const quadroRef = useRef(null);
  const fecharTimerRef = useRef(null);
  const fecharRef = useRef(onFechar);
  /** Desliga os listeners de toque do elemento atual (ver `ref`). */
  const desligarConsumoRef = useRef(null);

  /**
   * Dono do gesto no nível do **toque**.
   *
   * Os handlers de ponteiro não conseguem cancelar o gesto do navegador: o React
   * registra `touchmove` como passivo no root, então `preventDefault` ali não
   * vale. Este listener é nativo, no próprio elemento, com `{ passive: false }`.
   *
   * Por que cancelar: enquanto a sequência de toque de um arrasto **não** é
   * consumida, o Chromium a mantém "viva" e **descarta o `click` do toque
   * seguinte** (janela medida de ~300-500 ms). Era isso que obrigava a tocar
   * duas ou três vezes no sino depois do swipe. O cancelamento só acontece
   * depois de o eixo do swipe ser reconhecido, então a rolagem perpendicular
   * (lista vertical, chips horizontais) continua nativa.
   */
  const ref = useCallback(
    (node) => {
      desligarConsumoRef.current?.();
      desligarConsumoRef.current = null;

      if (!node || typeof node.addEventListener !== 'function') return;

      let inicio = null;

      const onTouchStart = (event) => {
        if (event.touches.length !== 1 || event.target?.closest?.('[data-no-swipe]')) {
          inicio = null;
          return;
        }

        const toque = event.touches[0];
        inicio = { x: toque.clientX, y: toque.clientY };
      };

      const onTouchMove = (event) => {
        if (!inicio || event.touches.length !== 1) return;

        const toque = event.touches[0];
        const dx = toque.clientX - inicio.x;
        const dy = toque.clientY - inicio.y;
        const principal = direcao === 'direita' ? dx : dy;
        const perpendicular = direcao === 'direita' ? dy : dx;

        if (Math.hypot(dx, dy) < TOLERANCIA) return;
        // Movimento predominantemente perpendicular: a rolagem é do navegador.
        if (Math.abs(principal) <= Math.abs(perpendicular) * RAZAO_EIXO) return;

        if (event.cancelable) event.preventDefault();
      };

      const onTouchEnd = () => {
        inicio = null;
      };

      node.addEventListener('touchstart', onTouchStart, { passive: true });
      node.addEventListener('touchmove', onTouchMove, { passive: false });
      node.addEventListener('touchend', onTouchEnd);
      node.addEventListener('touchcancel', onTouchEnd);

      desligarConsumoRef.current = () => {
        node.removeEventListener('touchstart', onTouchStart);
        node.removeEventListener('touchmove', onTouchMove);
        node.removeEventListener('touchend', onTouchEnd);
        node.removeEventListener('touchcancel', onTouchEnd);
      };
    },
    [direcao]
  );

  useEffect(() => {
    fecharRef.current = onFechar;
  }, [onFechar]);

  const cancelarQuadro = useCallback(() => {
    if (quadroRef.current) {
      cancelAnimationFrame(quadroRef.current);
      quadroRef.current = null;
    }
  }, []);

  /** Aplica o deslocamento no máximo uma vez por frame. */
  const aplicarOffset = useCallback(
    (valor) => {
      cancelarQuadro();
      quadroRef.current = requestAnimationFrame(() => setOffset(valor));
    },
    [cancelarQuadro]
  );

  const resetar = useCallback(() => {
    const gesto = gestoRef.current;
    gestoRef.current = null;
    cancelarQuadro();
    // Fechou/desmontou com o gesto vivo: devolve a captura antes de zerar.
    if (gesto) liberarPonteiro(gesto.alvo, gesto.pointerId);
    setArrastando(false);
    setFechando(false);
    setOffset(0);
  }, [cancelarQuadro]);

  const cancelarFechamento = useCallback(() => {
    if (fecharTimerRef.current) {
      clearTimeout(fecharTimerRef.current);
      fecharTimerRef.current = null;
    }
  }, []);

  /**
   * Fecha no próximo macrotask, nunca dentro do `pointerup`.
   *
   * No Chrome o `pointerup` chega ANTES do `touchend`. Remover o elemento que
   * recebeu o toque ali deixa a sequência de toque pela metade — e o overlay
   * pode desaparecer no meio do frame em que o dedo ainda está na tela.
   * Um macrotask depois, o `touchend` já foi entregue e a remoção é segura.
   */
  const agendarFechamento = useCallback(() => {
    cancelarFechamento();
    fecharTimerRef.current = setTimeout(() => {
      fecharTimerRef.current = null;
      fecharRef.current?.();
    }, 0);
  }, [cancelarFechamento]);

  // Qualquer mudança de abertura começa do zero. Abrir com deslocamento
  // residual (conteúdo fora da tela) era o que fazia a gaveta parecer "morta"
  // ao clicar no sino de novo depois de um swipe.
  useEffect(() => {
    resetar();
    return () => {
      // Desmontou com o dedo na tela: nenhum frame pode reaplicar o deslocamento.
      cancelarQuadro();
      cancelarFechamento();
      gestoRef.current = null;
    };
  }, [aberto, cancelarFechamento, cancelarQuadro, resetar]);

  const handlePointerDown = useCallback(
    (event) => {
      if (!aberto) return;
      if (apenasToque && event.pointerType === 'mouse') return;
      if (typeof event.button === 'number' && event.button !== 0) return;
      if (event.target?.closest?.('[data-no-swipe]')) return;

      const alvo = event.currentTarget;
      const rect = alvo?.getBoundingClientRect?.() || { width: 0, height: 0 };

      gestoRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        inicio: Date.now(),
        eixo: null,
        deslocamento: 0,
        tamanho: direcao === 'direita' ? rect.width : rect.height,
        pointerId: event.pointerId,
        alvo,
      };
    },
    [aberto, apenasToque, direcao]
  );

  const handlePointerMove = useCallback(
    (event) => {
      const gesto = gestoRef.current;
      if (!gesto || gesto.pointerId !== event.pointerId) return;

      const dx = event.clientX - gesto.startX;
      const dy = event.clientY - gesto.startY;
      const principal = direcao === 'direita' ? dx : dy;
      const perpendicular = direcao === 'direita' ? dy : dx;

      if (gesto.eixo === null) {
        if (Math.hypot(dx, dy) < TOLERANCIA) return;

        // Movimento predominantemente perpendicular: não é nosso gesto. Deixa
        // a rolagem nativa seguir sem interferência.
        if (Math.abs(principal) <= Math.abs(perpendicular) * RAZAO_EIXO) {
          gesto.eixo = 'outro';
          return;
        }

        gesto.eixo = 'principal';
        setArrastando(true);
        capturarPonteiro(gesto.alvo, event.pointerId);
      }

      if (gesto.eixo !== 'principal') return;

      const deslocamento = principal >= 0 ? principal : principal * resistencia;
      gesto.deslocamento = deslocamento;
      aplicarOffset(deslocamento);
    },
    [aplicarOffset, direcao, resistencia]
  );

  const finalizar = useCallback(
    (event, cancelado = false) => {
      const gesto = gestoRef.current;
      if (!gesto) return;
      if (event && event.pointerId !== undefined && gesto.pointerId !== event.pointerId) return;

      // A limpeza vem antes de qualquer chamada que possa lançar (`releasePointerCapture`):
      // com o estado já zerado, o pior caso é perder a animação de volta, nunca
      // ficar com o conteúdo preso fora da tela e o gesto "morto".
      gestoRef.current = null;
      cancelarQuadro();
      setArrastando(false);

      if (event?.pointerId !== undefined) liberarPonteiro(gesto.alvo, event.pointerId);

      if (cancelado || gesto.eixo !== 'principal') {
        setOffset(0);
        return;
      }

      const limiteEfetivo = Math.min(
        limite,
        Math.max(LIMITE_MINIMO, gesto.tamanho * limiteRelativo)
      );
      const duracao = Math.max(JANELA_VELOCIDADE_MS, Date.now() - gesto.inicio);
      const velocidade = gesto.deslocamento / duracao;
      // Um "chacoalhão" rápido também fecha, mas nunca abaixo de um mínimo:
      // sem esse piso, qualquer toque com 2px de tremor dispararia o fechamento
      // no primeiro frame.
      const minimoParaVelocidade = Math.max(32, limiteEfetivo * 0.4);
      const fechouPelaVelocidade =
        velocidade >= VELOCIDADE_FECHAR && gesto.deslocamento >= minimoParaVelocidade;
      const deveFechar = gesto.deslocamento >= limiteEfetivo || fechouPelaVelocidade;

      if (deveFechar && fecharRef.current) {
        // O painel completa a saída (sem animação) e o `onFechar` vai para o
        // próximo macrotask: o overlay só pode sair do DOM depois que o
        // navegador terminou a sequência de toque (ver `agendarFechamento`).
        setFechando(true);
        setOffset(gesto.tamanho || 0);
        if (haptic) vibrar('leve');
        agendarFechamento();
        return;
      }

      setOffset(0);
    },
    [agendarFechamento, cancelarQuadro, haptic, limite, limiteRelativo]
  );

  // O `style` existe sempre (mesmo parado) para que a volta ao normal seja
  // animada: devolver `undefined` no `setOffset(0)` removia o `transform` e o
  // `transition` de uma vez, então o conteúdo pulava para a origem.
  const style = {
    transform:
      direcao === 'direita' ? `translate3d(${offset}px, 0, 0)` : `translate3d(0, ${offset}px, 0)`,
    transition: arrastando || fechando || prefereMenosMovimento() ? 'none' : TRANSICAO_VOLTA,
    ...(arrastando || fechando ? { willChange: 'transform' } : {}),
  };

  return {
    /** Vai no elemento arrastável; liga o consumo do `touchmove` (ver `ref`). */
    ref,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: (event) => finalizar(event),
      onPointerCancel: (event) => finalizar(event, true),
    },
    offset,
    arrastando,
    style,
  };
};

export default useArrastarParaFechar;
